import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isNativePlatform, checkBiometryAvailable, authenticateWithBiometrics } from '../lib/biometricAuth';

const ENABLED_KEY = 'agence_biometric_lock_enabled';

// How long the app can sit backgrounded before a resume re-triggers the lock screen.
// Cold launch always locks (if enabled) regardless of this value.
export const BACKGROUND_LOCK_TIMEOUT_MS = 2 * 60 * 1000;

export function isBiometricLockEnabled() {
  return localStorage.getItem(ENABLED_KEY) === '1';
}

export function setBiometricLockEnabled(enabled) {
  if (enabled) localStorage.setItem(ENABLED_KEY, '1');
  else localStorage.removeItem(ENABLED_KEY);
}

/**
 * Device-local convenience lock (docs/TODO.md §1a) — never a real access-control boundary,
 * TOTP stays the sole real 2FA. Rendered unconditionally inside AuthProvider (alongside
 * AuthShell) so the background-timeout listener persists across login/logout, but it only
 * ever shows its overlay while actually authenticated.
 *
 * Locks on cold launch (once per app process, the first time auth resolves signed-in) and
 * again on resume from background past BACKGROUND_LOCK_TIMEOUT_MS.
 *
 * No biometry available at all falls through immediately — there's nothing to check against,
 * so it must never be able to strand someone outside their own app. A real attempt that fails
 * or is cancelled, though, does NOT unlock (confirmed real bug, 2026-09-11: covering the
 * camera to intentionally fail Face ID still unlocked the app, because the old code discarded
 * authenticateWithBiometrics()'s return value entirely) — it re-prompts, with a "Sign Out"
 * escape hatch so a genuinely broken sensor can never lock someone out of their own account
 * (signing out and back in via the normal TOTP/OAuth path always works).
 */
export default function BiometricLockGate() {
  const { isAuthenticated, initializing, logout } = useAuth();
  const [locked, setLocked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const hasCheckedColdLaunch = useRef(false);
  const backgroundedAtRef = useRef(null);

  useEffect(() => {
    if (hasCheckedColdLaunch.current || initializing) return;
    hasCheckedColdLaunch.current = true;
    if (isNativePlatform() && isAuthenticated && isBiometricLockEnabled()) {
      setFailed(false);
      setLocked(true);
    }
  }, [isAuthenticated, initializing]);

  useEffect(() => {
    if (!isNativePlatform()) return undefined;
    let handle;
    let cancelled = false;
    import('@capacitor/app').then(({ App }) => {
      if (cancelled) return;
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          backgroundedAtRef.current = Date.now();
          return;
        }
        const backgroundedAt = backgroundedAtRef.current;
        backgroundedAtRef.current = null;
        if (!backgroundedAt) return;
        if (Date.now() - backgroundedAt >= BACKGROUND_LOCK_TIMEOUT_MS && isBiometricLockEnabled()) {
          setFailed(false);
          setLocked(true);
        }
      }).then(h => { handle = h; }).catch(() => {});
    }).catch(() => {});
    return () => { cancelled = true; handle?.remove(); };
  }, []);

  useEffect(() => {
    if (!locked) return undefined;
    let cancelled = false;
    (async () => {
      const available = await checkBiometryAvailable();
      if (!available) {
        if (!cancelled) setLocked(false);
        return;
      }
      const success = await authenticateWithBiometrics();
      if (cancelled) return;
      if (success) setLocked(false);
      else setFailed(true);
    })();
    return () => { cancelled = true; };
  }, [locked, attempt]);

  if (!locked || !isAuthenticated) return null;

  return (
    <div className="biometric-lock-overlay" role="dialog" aria-modal="true" aria-label="Unlock Agence">
      {failed ? (
        <div className="biometric-lock-card">
          <div className="biometric-lock-icon" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="9" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              <line x1="12" y1="14.5" x2="12" y2="16.5" />
            </svg>
          </div>
          <h2 className="biometric-lock-title">Authentication failed</h2>
          <p className="biometric-lock-subtitle">We couldn&apos;t verify it&apos;s you. Try again, or sign out and log back in.</p>
          <div className="biometric-lock-actions">
            <button
              type="button"
              className="btn-primary cta-btn"
              onClick={() => { setFailed(false); setAttempt(a => a + 1); }}
            >
              Try Again
            </button>
            <button type="button" className="biometric-lock-signout" onClick={() => logout()}>Sign Out</button>
          </div>
        </div>
      ) : (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Unlocking…</p>
      )}
    </div>
  );
}
